import { useState, useEffect, useRef } from "react";

interface InlineSelectProps {
  value: string;
  placeholder: string;
  options: any[];
  onChange: (v: any) => void;
  disabled?: boolean;
}

export default function InlineSelect({ value, placeholder, options, onChange, disabled }: InlineSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const displayVal = value || placeholder;
  const isPlaceholder = !value;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`flex items-center gap-1 border rounded px-2 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap shadow-sm w-full justify-between ${
          disabled
            ? "bg-slate-50 border-slate-150 text-slate-400 cursor-not-allowed"
            : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
        }`}
      >
        <span className={`truncate ${isPlaceholder ? "text-slate-400" : "text-slate-700"}`}>
          {displayVal}
        </span>
        {!disabled && (
          <i className={`ri-arrow-down-s-line text-slate-400 transition-transform text-xs flex-shrink-0 ${open ? "rotate-180" : ""}`} />
        )}
      </button>
      {open && (
        <div className="absolute top-7 left-0 z-50 min-w-24 bg-white border border-slate-200 rounded shadow-lg overflow-hidden max-h-44 overflow-y-auto">
          <button
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50 whitespace-nowrap transition-colors cursor-pointer border-b border-slate-100"
          >
            {placeholder}
          </button>
          {options.map((opt) => (
            <button
              key={opt.code}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                value === opt.gid
                  ? "text-slate-900 bg-slate-100 font-medium"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {opt.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}